import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, getApiError } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { EditTxnDateDialog } from "@/components/EditTxnDateDialog";
import { TransactionsTable } from "@/components/TransactionsTable";
import { RecordTxnForm } from "@/components/RecordTxnForm";
import { EditCustomerForm } from "@/components/EditCustomerForm";
import { DeleteCustomerCard } from "@/components/DeleteCustomerCard";
import { StatementButton } from "@/components/StatementButton";
import { shareTxnOnWhatsApp } from "@/lib/whatsapp";

const CustomerSummary = ({ customer }) => (
  <section className="bg-white border border-slate-200 rounded-lg p-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
    <div>
      <p className="label-overline text-slate-500">{customer.mobile}</p>
      <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-2">{customer.name}</h1>
      <p className="text-slate-500 mt-1 text-sm">Joined {formatDate(customer.created_at)}</p>
    </div>
    <div className="text-right">
      <p className="label-overline text-slate-500">Current balance</p>
      <p data-testid="detail-balance-amount" className="font-mono-num text-4xl font-semibold text-slate-900 mt-2">
        {formatINR(customer.balance)}
      </p>
    </div>
  </section>
);

const TxnHistory = ({ customer, txns, onChanged }) => {
  const [editingTxn, setEditingTxn] = useState(null);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-slate-900">Transaction history</h3>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="font-mono-num">{txns.length} entries</Badge>
          <StatementButton customer={customer} size="sm" testid="admin-download-statement-btn"
            endpoint={`/admin/customers/${customer.id}/transactions`} />
        </div>
      </div>
      <TransactionsTable txns={txns} onEditDate={setEditingTxn} onShare={(t) => shareTxnOnWhatsApp(customer, t)}
        balanceLabel="Balance after" testid="admin-customer-txn-table" />
      {editingTxn && (
        <EditTxnDateDialog customerId={customer.id} txn={editingTxn} onClose={() => setEditingTxn(null)}
          onSaved={() => { setEditingTxn(null); onChanged(); }} />
      )}
    </div>
  );
};

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [txns, setTxns] = useState([]);

  const load = useCallback(async () => {
    try {
      const [c, t] = await Promise.all([
        api.get(`/admin/customers/${id}`),
        api.get(`/admin/customers/${id}/transactions`),
      ]);
      setCustomer(c.data);
      setTxns(t.data.transactions || []);
    } catch (e) {
      toast.error(getApiError(e));
      navigate("/admin");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (!customer) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <span className="label-overline">Loading customer…</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="back-to-admin" className="gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to customers
          </Button>
          <Badge variant="outline" className="label-overline">Customer detail</Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8" data-testid="admin-customer-detail">
        <CustomerSummary customer={customer} />
        <Tabs defaultValue="txn" className="w-full">
          <TabsList className="bg-slate-100" data-testid="customer-detail-tabs">
            <TabsTrigger value="txn" data-testid="tab-txn">Transactions</TabsTrigger>
            <TabsTrigger value="edit" data-testid="tab-edit">Edit details</TabsTrigger>
            <TabsTrigger value="danger" data-testid="tab-danger">Danger zone</TabsTrigger>
          </TabsList>
          <TabsContent value="txn" className="space-y-6 mt-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <p className="label-overline text-slate-500">Record transaction</p>
              <RecordTxnForm customer={customer} onRecorded={load} />
            </div>
            <TxnHistory customer={customer} txns={txns} onChanged={load} />
          </TabsContent>
          <TabsContent value="edit" className="mt-6">
            <EditCustomerForm key={customer.id} customer={customer} onSaved={load} />
          </TabsContent>
          <TabsContent value="danger" className="mt-6">
            <DeleteCustomerCard customer={customer} onDeleted={() => navigate("/admin")} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
